import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    getVehicleBookingDataWithId,
    updateVehicleBooking,
    getDriversList,
    getCustomersList,
    getFlightsList,
    getVehiclesList,
} from "adminApi/TransportFacilityApi/VehicleBookingApi.js";
import { validateForm } from "utils/validation";
import { VehicleBookingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import EditVehicleBookingForm from "./components/EditVehicleBookingForm";

const EditVehicleBooking = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { values, setValues, errors, handleChange, setErrors } = useFormChange(
        {
            vehicle_id: "",
            booked_id: "",
            flight_id: "",
            driver_id: "",
            transportation_type: "",
            transportation_time: "",
            cost: 0,
            is_free: "0",
            customer_name_to_show: "",
        },
        VehicleBookingRules
    );

    // ── Lookup data for dropdowns ──────────────────────────────────────────
    const { data: drivers = [] } = useFetchAllData(getDriversList);
    const { data: vehicles = [] } = useFetchAllData(getVehiclesList);
    const { data: flights = [] } = useFetchAllData(getFlightsList);
    const { data: customers = [] } = useFetchAllData(getCustomersList);

    // Fetch existing booking record by ID to pre-fill the form
    const fetchBooking = useMemo(() => () => getVehicleBookingDataWithId(id), [id]);
    const { data: bookingData, loading: fetchLoading } = useFetchAllData(fetchBooking);

    // Memoised option arrays — only recompute when source data changes
    const driverOptions = useMemo(() => drivers.map(d => ({ label: `${d.driver_name} / ${d.driver_phone}`, value: d.driver_id })), [drivers]);
    const vehicleOptions = useMemo(() => vehicles.map(v => ({ label: `${v.vehicle_number} (${v.vehicle_type})`, value: v.vehicle_id })), [vehicles]);
    const flightOptions = useMemo(() => flights.map(f => ({ label: f.flight_number, value: f.flight_id })), [flights]);
    const bookingOptions = useMemo(() => customers.map(c => ({ label: c.booking_number, value: c.booked_id })), [customers]);

    // ── Side effects ───────────────────────────────────────────────────────

    // Pre-fill form once the existing booking data is loaded
    useEffect(() => {
        if (bookingData) {
            setValues(bookingData);
        }
    }, [bookingData, setValues]);

    // Auto-fill customer name when booking selection changes
    useEffect(() => {
        if (values.booked_id) {
            const match = customers.find(c => Number(c.booked_id) === Number(values.booked_id));
            setValues(prev => ({
                ...prev,
                customer_name_to_show: match ? match.customer_name : "",
            }));
        } else {
            setValues(prev => ({ ...prev, customer_name_to_show: "" }));
        }
    }, [values.booked_id, customers, setValues]);

    // Force cost to 0 when "Is Free" is toggled on
    useEffect(() => {
        if (Number(values.is_free) === 1) {
            setValues(prev => ({ ...prev, cost: 0 }));
        }
    }, [values.is_free, setValues]);

    // ── Form submit ────────────────────────────────────────────────────────
    const { loading: updateLoading, handleSubmit } = useFormSubmit(
        async (formValues) => await updateVehicleBooking({ id, data: formValues }),
        (vals) => {
            const err = validateForm(vals, VehicleBookingRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Vehicle Booking Updated Successfully"
    );

    const loading = fetchLoading || updateLoading;

    return (
        <>
            {loading && <Spinner isSpinner />}
            <EditVehicleBookingForm
                values={values}
                errors={errors}
                flights={flights}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                driverOptions={driverOptions}
                vehicleOptions={vehicleOptions}
                flightOptions={flightOptions}
                bookingOptions={bookingOptions}
            />
        </>
    );
};

export default EditVehicleBooking;
